/**
 * Base interface for all GA4 events
 */
export interface BaseGA4Event {
  eventName: string;
  parameters?: Record<string, string | number | boolean | any>;
}

/**
 * Page View Event Interface
 * @description Enhanced page view event with custom parameters
 */
export interface PageViewEvent extends BaseGA4Event {
  pagePath: string;
  pageTitle: string;
  parameters?: {
    content_category?: string;
    user_type?: string;
    page_location?: string;
    event_id?: string;
    club_name?: string;
    order_id?: string;
    value?: number;
    timestamp?: string;
    [key: string]: string | number | boolean | undefined;
  };
}

/**
 * Custom Event Interface
 * @description Generic custom event with flexible parameters
 */
export interface CustomEvent extends BaseGA4Event {
  eventName: string;
  parameters?: {
    event_category?: string;
    event_label?: string;
    value?: number;
    user_type?: string;
    timestamp?: string;
    [key: string]: string | number | boolean | undefined;
  };
}

/**
 * E-commerce Event Interface
 * @description E-commerce related events (purchases, checkouts, etc.)
 */
export interface EcommerceEvent extends BaseGA4Event {
  parameters: EcommerceEventParameters;
}

/**
 * E-commerce Event Parameters Interface
 * @description Type-safe parameters for e-commerce events
 */
export interface EcommerceEventParameters {
  currency: string;
  value: number;
  transaction_id?: string;
  items?: EcommerceItem[];
  user_type?: string;
  timestamp?: string;
  [key: string]: string | number | boolean | EcommerceItem[] | undefined;
}

/**
 * E-commerce Item Interface
 * @description Individual item in an e-commerce transaction
 */
export interface EcommerceItem {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
  item_category?: string;
  item_variant?: string;
}

/**
 * User Properties Interface
 * @description User-level properties for GA4
 */
export interface UserProperties {
  user_id?: string | null;
  user_type?: string;
  [key: string]: string | number | boolean | null | undefined;
}

/**
 * Analytics Configuration Interface
 * @description Configuration options for the analytics service
 */
export interface AnalyticsConfig {
  measurementId: string;
  production: boolean;
  enableDebugLogging?: boolean;
  enableErrorLogging?: boolean;
}
